import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Link,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import React, { ChangeEvent, FC, useContext, useEffect, useState } from 'react';
import { useMutation } from 'react-query';
import ForgotPassword from './ForgotPassword';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router';
import { UserContext } from '../..';
import {
  LOGIN_LOCAL_STORAGE_KEY,
  REMEMBER_ME_LOCAL_STORAGE_KEY,
  SESSION_STORAGE_ACTIVE_KEY,
  SESSION_STORAGE_JWT_TOKEN,
} from './constants';
import { SESSION_STORAGE_SET_PASSWORD } from '../RetrievePassword';
import { BrandScreen } from '../../components/Brand';
import {
  ON_PRIMARY_FIELD_SX,
  ON_PRIMARY_LABEL_SX,
} from '../../components/fieldStyles';
import { colors } from '../../theme/tokens';

// XD screen 1 ("1. Login" in DESIGN.md): full-screen blue
// background, orange circles in the lower corners and a centred block
// with brand, Identifiant, Mot de passe, "Se souvenir de moi" and
// "Mot de passe oublié". The background, the circles and the brand are
// components/Brand/BrandScreen, shared by public verification,
// "Nouveau mot de passe" and the error screens.
//
// Decisions where the XD gives no data:
//   - The artboard does not draw a submit button (gap 7). "Se connecter" is added
//     as the primary orange button, `type="submit"`, so that the form is
//     usable without relying on Enter. Pending confirmation with the client.
//   - There are no drawn error states (gap 8). The messages go in
//     light orange (`#FF8B6F`, the XD link colour) because MUI's default
//     red does not read over the blue.
//   - Mobile (ROADMAP, Part 2): clipped circles and full-width block
//     with a margin (in BrandScreen).
//
// The behaviour is the usual one: client-side validation, messages in
// French according to the HTTP status, "Se souvenir de moi" in localStorage, session
// in sessionStorage, ForgotPassword dialog, and the ids `#login` / `#password`
// that the Cypress specs drive.

// Field and label over blue: see components/fieldStyles, shared
// with the XD modals.
const FIELD_SX = ON_PRIMARY_FIELD_SX;

const LABEL_SX = { ...ON_PRIMARY_LABEL_SX, mb: 0.75 } as const;

const Login: FC = () => {
  const [errorMessage, setErrorMessage] = useState('');
  const [emailError, setEmailError] = useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = useState('');
  const [openForgot, setOpenForgot] = useState(false);
  const [isOpenSnackbar, setIsOpenSnackbar] = useState(
    !!sessionStorage.getItem(SESSION_STORAGE_SET_PASSWORD),
  );
  const userContext = useContext(UserContext);
  const navigate = useNavigate();
  const [me] = useState(
    localStorage.getItem(REMEMBER_ME_LOCAL_STORAGE_KEY) &&
      localStorage.getItem(REMEMBER_ME_LOCAL_STORAGE_KEY) === 'true'
      ? localStorage.getItem(LOGIN_LOCAL_STORAGE_KEY)
      : '',
  );

  useEffect(() => {
    document.title = 'ArchivOs - Login';
  }, []);

  useEffect(() => {
    const shouldRememberMe = localStorage.getItem(
      REMEMBER_ME_LOCAL_STORAGE_KEY,
    );
    const isSessionActive = sessionStorage.getItem(SESSION_STORAGE_ACTIVE_KEY);

    if (shouldRememberMe === 'true' || isSessionActive === 'true') {
      const localStorageUser = localStorage.getItem(LOGIN_LOCAL_STORAGE_KEY);
      if (localStorageUser) {
        userContext?.updateUser(JSON.parse(localStorageUser));
        window.location.href = '#';
      }
    } else {
      navigate('/login');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Plain fetch on purpose: there is no token yet, and archivosFetch would attach
  // "Bearer null".
  const mutation = useMutation({
    mutationFn: (data: Record<string, string>) => {
      return fetch(`${process.env.REACT_APP_API_URL}/api/users/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
    },
  });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage('');
    if (!emailError && !passwordError) {
      const data = new FormData(event.currentTarget);

      const login = data.get('login') as string;

      const sendData = {
        email: login,
        password: data.get('password') as string,
      };

      mutation.mutateAsync(sendData).then(async (response) => {
        if (response.status === 401) {
          setErrorMessage('Mauvais login ou mot de passe');
        } else if (response.status === 404) {
          setErrorMessage('Identifiants invalides');
        } else {
          setErrorMessage('');
        }
        if (response.status === 200) {
          const responseUser = await response.json();
          userContext?.updateUser(responseUser.user);
          sessionStorage.setItem(SESSION_STORAGE_JWT_TOKEN, responseUser.token);
          localStorage.setItem(
            LOGIN_LOCAL_STORAGE_KEY,
            JSON.stringify(responseUser.user),
          );
          sessionStorage.setItem(SESSION_STORAGE_ACTIVE_KEY, 'true');
          navigate('/my-documents');
        }
      });
    }
  };

  const handleClickForgot = () => {
    setOpenForgot(true);
  };

  const validateInputs = () => {
    const email = document.getElementById('login') as HTMLInputElement;
    const password = document.getElementById('password') as HTMLInputElement;
    let isValid = true;

    if (!email.value) {
      setEmailError(true);
      setEmailErrorMessage('Veuillez entrer une adresse mail.');
      isValid = false;
    } else {
      setEmailError(false);
      setEmailErrorMessage('');
    }

    if (!password.value) {
      setPasswordError(true);
      setPasswordErrorMessage('Veuillez entrer un mot de passe.');
      isValid = false;
    } else {
      setPasswordError(false);
      setPasswordErrorMessage('');
    }

    return isValid;
  };

  const handleChangeRememberMe = (e: ChangeEvent<HTMLInputElement>) => {
    localStorage.setItem(
      REMEMBER_ME_LOCAL_STORAGE_KEY,
      e.target.checked.toString(),
    );
  };

  return me ? null : (
    <>
      <BrandScreen>
        <Box
          component="form"
          onSubmit={handleSubmit}
          noValidate
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5,
            mt: 5,
          }}
        >
          <FormControl fullWidth error={emailError}>
            <FormLabel htmlFor="login" sx={LABEL_SX}>
              Identifiant
            </FormLabel>
            <TextField
              id="login"
              error={emailError}
              helperText={emailErrorMessage}
              type="email"
              name="login"
              autoComplete="username"
              autoFocus
              required
              fullWidth
              placeholder="abcd@dddddd.fr"
              variant="outlined"
              sx={FIELD_SX}
            />
          </FormControl>

          <FormControl fullWidth error={passwordError}>
            <FormLabel htmlFor="password" sx={LABEL_SX}>
              Mot de passe
            </FormLabel>
            <TextField
              error={passwordError}
              helperText={passwordErrorMessage}
              name="password"
              type="password"
              id="password"
              autoComplete="current-password"
              required
              fullWidth
              variant="outlined"
              sx={FIELD_SX}
            />
          </FormControl>

          {errorMessage && (
            <Typography
              role="alert"
              variant="body1"
              sx={{ color: colors.accentLight }}
            >
              {errorMessage}
            </Typography>
          )}

          {/* Both XDs stack these: the checkbox row, then "Mot de passe
              oublié" on its own line, left-aligned (S1 and the Flux 2
              login alike). */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 1,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  onChange={handleChangeRememberMe}
                  value="remember"
                  defaultChecked={
                    localStorage.getItem(REMEMBER_ME_LOCAL_STORAGE_KEY) ===
                    'true'
                  }
                  sx={{
                    color: colors.white,
                    '&.Mui-checked': { color: colors.accentLight },
                  }}
                />
              }
              label="Se souvenir de moi"
              sx={{
                mr: 0,
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.875rem',
                  color: colors.white,
                },
              }}
            />

            <Link
              component="button"
              type="button"
              onClick={handleClickForgot}
              variant="actionLink"
              underline="hover"
              sx={{ color: colors.accentLight }}
            >
              Mot de passe oublié
            </Link>
          </Box>

          {/* Button not drawn in the XD (gap 7): see header note. */}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="secondary"
            size="large"
            sx={{ mt: 1 }}
            onClick={validateInputs}
          >
            Se connecter
          </Button>
        </Box>

        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{
            '& .MuiPaper-root': {
              backgroundColor: 'white',
              color: 'green',
            },
          }}
          open={
            isOpenSnackbar &&
            sessionStorage.getItem(SESSION_STORAGE_SET_PASSWORD) === 'true'
          }
          autoHideDuration={6000}
          onClose={() => {
            setIsOpenSnackbar(false);
            sessionStorage.removeItem(SESSION_STORAGE_SET_PASSWORD);
          }}
          action={
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={() => {
                setIsOpenSnackbar(false);
                sessionStorage.removeItem(SESSION_STORAGE_SET_PASSWORD);
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
          message={'Le mot de passe a été changé avec succès.'}
        />
      </BrandScreen>
      <ForgotPassword open={openForgot} onClose={() => setOpenForgot(false)} />
    </>
  );
};

export default Login;
